/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { t } from 'i18next';

import { ReactSVG } from 'react-svg';

import { styled } from '@mui/material/styles';

import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';

import { Box, Popover } from '@mui/material';
import { KeyboardArrowDown, Search } from '@mui/icons-material';

import { useFormContext } from '../../../contexts/form';

import { MaskInput } from './MaskInput';

import useInputField from './hooks';
import privateStyles from './styles';

const InputFieldRaw = ({
  className, onChangeInput, name, value, type, disabled, label, required,
  validate, pattern, clipboard, errorPattern, autoFocus, rows, placeholder, hideLabel,
  renderLabelEnd, inputProps, actionButtons, MaskInputProps, ...props
}) => {
  const { formErrors, formRegister } = useFormContext();
  const {
    open,
    menuEl,
    fakeType,
    popperOpen,
    handleFocus,
    handleCloseMenu,
    handleTooltipOpen,
    handleInputMouseDown,
    handleClickShowPassword,
  } = useInputField({ value, type });

  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState();

  useEffect(() => {
    setAnchorEl(inputRef);
  }, [inputRef]);

  const labelComponent = (
    <>
      {t(label)}
      {renderLabelEnd?.({ inputRef: anchorEl })}
    </>
  );

  const boxClasses = {
    [className]: true,
    'DN-InputField': true,
    'dn-open': popperOpen,
    'dn-hide-label': hideLabel,
    'dn-clipboard': clipboard,
  };

  const register = formRegister(name, {
    onChange: (e) => onChangeInput(e),
    required: {
      value: required,
      message: `${t(label)} ${t('IS REQUIRED')}`,
    },
    pattern: {
      value: new RegExp(pattern),
      message: errorPattern,
    },
    validate: { ...validate },
  });

  return (
    <Box ref={inputRef} className={boxClasses}>
      <TextField
        {...register}
        onFocus={handleFocus}
        onMouseDown={handleInputMouseDown}
        name={name}
        value={value}
        label={labelComponent}
        required={required}
        type={fakeType || 'text'}
        placeholder={t(placeholder)}
        error={!!formErrors[name]}
        helperText={formErrors[name] ? t(formErrors[name].message) : ''}
        autoFocus={autoFocus}
        disabled={clipboard || disabled}
        rows={rows}
        fullWidth
        autoComplete="off"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          inputComponent: MaskInputProps ? MaskInput : undefined,
          inputProps: MaskInputProps ? {
            ...MaskInputProps,
            register,
          } : {
            min: -Number.MAX_SAFE_INTEGER,
            max: Number.MAX_SAFE_INTEGER,
          },
          _lpignore: 'true',
          'data-lpignore': true,
          autoComplete: 'new-password',
          endAdornment:
            ['password', 'text_date', 'search', 'select'].includes(type) && (
              <InputAdornment position="end">
                {
                  type === 'password' && (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {fakeType !== 'password' ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }
                {
                  type === 'text_date' && (
                    <DateRangeOutlinedIcon />
                  )
                }
                {
                  type === 'search' && (
                    <Search />
                  )
                }
                {
                  type === 'select' && (
                    <IconButton
                      className="dn-button-small"
                      edge="end"
                    >
                      <KeyboardArrowDown />
                    </IconButton>
                  )
                }
              </InputAdornment>
            ),
          ...inputProps,
        }}
        {...props}
      />
      {
        clipboard && (
          <Box className="dn-clipboard">
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              open={open}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={t('Copied!')}
            >
              <Button className="dn-button-clipboard" variant="contained" onClick={handleTooltipOpen}>
                <ReactSVG className="dn-svg" src="https://resources.datanomik.com/dashboard/images/copy.svg" />
              </Button>
            </Tooltip>
          </Box>
        )
      }
      {
        actionButtons && menuEl && (
          <Popover
            id="basic-menu"
            anchorEl={menuEl}
            open={popperOpen}
            PaperProps={{
              elevation: 0,
              style: {
                width: menuEl?.getBoundingClientRect()?.width,
                background: 'none',
                borderRadius: '0',
              },
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            onClose={handleCloseMenu}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
          >
            { actionButtons(handleCloseMenu) }
          </Popover>
        )
      }
    </Box>

  );
};

InputFieldRaw.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChangeInput: PropTypes.func,
  validate: PropTypes.object,
  type: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  pattern: PropTypes.string,
  errorPattern: PropTypes.string,
  clipboard: PropTypes.bool,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  rows: PropTypes.number,
  hideLabel: PropTypes.bool,
  renderLabelEnd: PropTypes.func,
  actionButtons: PropTypes.func,
  inputProps: PropTypes.object,
  MaskInputProps: PropTypes.object,
};

const InputField = styled(InputFieldRaw)(privateStyles);

InputField.propTypes = InputFieldRaw.propTypes;

export default InputField;
